// Groups permissions two levels deep: the area (the permission's category) and, inside it, the
// second part of the key. It stops there on purpose, so `admin:platform:flags:write` sits in the
// "platform" group of "admin" rather than in a group of its own.

export interface GroupablePermission {
  key: string;
  category?: string;
}

export interface PermissionGroup<T extends GroupablePermission> {
  /** Second part of the key, or '' for permissions with no second level (General). */
  group: string;
  items: T[];
}

export interface PermissionArea<T extends GroupablePermission> {
  category: string;
  count: number;
  groups: PermissionGroup<T>[];
}

export const GENERAL_GROUP = '';

export function permissionCategory(p: GroupablePermission): string {
  if (p.key === '*') return 'everything';
  return p.category || p.key.split(':')[0] || 'other';
}

/** `admin:users:read` -> `users`; `permissions:read`, `eng:env-copy` and `admin:*` -> General. */
export function permissionGroup(key: string): string {
  const parts = key.split(':');
  return parts.length >= 3 && parts[1] ? parts[1] : GENERAL_GROUP;
}

const LABELS: Record<string, string> = { sso: 'SSO', eva: 'Eva' };

export function groupLabel(group: string): string {
  if (group === GENERAL_GROUP) return 'General';
  if (LABELS[group]) return LABELS[group];
  const words = group.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// The group's own "full access" key (`admin:users:*`, or `eng:*` for General) comes first. Deeper
// wildcards such as `admin:companies:domain:*` stay next to the keys they cover.
function isGroupWildcard(key: string): boolean {
  const parts = key.split(':');
  return parts[parts.length - 1] === '*' && parts.length <= 3;
}

function compareKeys(a: string, b: string): number {
  const aWild = isGroupWildcard(a);
  const bWild = isGroupWildcard(b);
  if (aWild !== bWild) return aWild ? -1 : 1;
  return a.localeCompare(b);
}

/** Areas sorted by name; inside each, General first and then groups by name. */
export function groupPermissions<T extends GroupablePermission>(
  permissions: T[],
): PermissionArea<T>[] {
  const areas = new Map<string, Map<string, T[]>>();
  for (const p of permissions) {
    const category = permissionCategory(p);
    const group = permissionGroup(p.key);
    let groups = areas.get(category);
    if (!groups) {
      groups = new Map();
      areas.set(category, groups);
    }
    const items = groups.get(group);
    if (items) items.push(p);
    else groups.set(group, [p]);
  }
  return Array.from(areas.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([category, groups]) => {
      const sorted = Array.from(groups.entries())
        .sort(([a], [b]) =>
          a === GENERAL_GROUP ? -1 : b === GENERAL_GROUP ? 1 : a.localeCompare(b),
        )
        .map(([group, items]) => ({
          group,
          items: [...items].sort((x, y) => compareKeys(x.key, y.key)),
        }));
      return {
        category,
        count: sorted.reduce((n, g) => n + g.items.length, 0),
        groups: sorted,
      };
    });
}
